/**
 * React Query hooks for fetching configuration from backend
 */

import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../api/client'

export interface SchedulingConfig {
  min_buffer_minutes: number
  default_task_duration: number
  anchor_task_types: string[]
  max_delay_minutes: number
  critical_path_threshold: number
  max_concurrent_suppliers: number
  setup_buffer_minutes: number
  breakdown_buffer_minutes: number
}

export interface TaskCategories {
  categories: string[]
  priority_levels: Record<string, number>
}

export interface NotificationConfig {
  delay_notification_threshold_minutes: number
  early_arrival_threshold_minutes: number
  supplier_check_in_window_minutes: number
  reminder_intervals_minutes: number[]
}

export interface ValidationRules {
  min_task_duration_minutes: number
  max_task_duration_minutes: number
  min_event_duration_hours: number
  max_event_duration_hours: number
  max_tasks_per_supplier: number
  max_suppliers_per_event: number
}

export interface AllConfig {
  scheduling: SchedulingConfig
  task_categories: TaskCategories
  notifications: NotificationConfig
  validation: ValidationRules
  feature_flags: Record<string, boolean>
}

/**
 * Hook to fetch scheduling configuration
 */
export const useSchedulingConfig = () => {
  return useQuery<SchedulingConfig>({
    queryKey: ['config', 'scheduling'],
    queryFn: async () => {
      const response = await apiClient.get('/config/scheduling')
      return response.data
    },
    staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
    gcTime: 24 * 60 * 60 * 1000,
  })
}

/**
 * Hook to fetch task categories
 */
export const useTaskCategories = () => {
  return useQuery<TaskCategories>({
    queryKey: ['config', 'task-categories'],
    queryFn: async () => {
      const response = await apiClient.get('/config/task-categories')
      return response.data
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  })
}

/**
 * Hook to fetch notification configuration
 */
export const useNotificationConfig = () => {
  return useQuery<NotificationConfig>({
    queryKey: ['config', 'notifications'],
    queryFn: async () => {
      const response = await apiClient.get('/config/notifications')
      return response.data
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  })
}

/**
 * Hook to fetch validation rules
 */
export const useValidationRules = () => {
  return useQuery<ValidationRules>({
    queryKey: ['config', 'validation'],
    queryFn: async () => {
      const response = await apiClient.get('/config/validation')
      return response.data
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  })
}

/**
 * Hook to fetch all configuration in one call
 */
export const useAllConfig = () => {
  return useQuery<AllConfig>({
    queryKey: ['config', 'all'],
    queryFn: async () => {
      const response = await apiClient.get('/config/all')
      return response.data
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  })
}

/**
 * Helper to get config values with fallback to hardcoded defaults
 * This allows gradual migration from hardcoded to API-based config
 */
export const getConfigWithFallback = <T>(
  configData: T | undefined,
  isLoading: boolean,
  fallbackValue: T
): T => {
  if (isLoading || !configData) {
    return fallbackValue
  }
  return configData
}